import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { AirlineIcon, Icon } from "@b2bportal/core-ui";
import { DateTimeFormatStyle, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./styles.module.scss";

export type FlightSegmentCardProps = {
  // time represented in flight departure location timezone
  departureTime: string;
  // time represented in flight arrival location timezone
  arrivalTime: string;
  airlineCode: string;
  airlineName: string;
  flightNumber: string;
  // cabin of this segment, shown so mixed-cabin itineraries are visible per leg
  cabinClassName?: string;
  className?: string;
};

/**
 * Used in HSBC flight checkout seat, baggage, review, et. to show basic flight segment info
 */
export const FlightSegmentCard = (props: FlightSegmentCardProps) => {
  const {
    departureTime,
    arrivalTime,
    airlineCode,
    airlineName,
    flightNumber,
    cabinClassName,
  } = props;

  const [block, cn] = useModuleBEM(styles, "FlightSegmentCard");
  const { t, formatDateTime, formatInterval } = useI18nContext();

  // plusDays / arrivalDateLabel compare airport-local calendar days
  // (offset stripped) so a viewer in any browser timezone sees the same
  // "+N day" indicator the boarding pass would show. Without removeTimezone
  // the diff would reflect the user's local timezone instead of the trip's.
  const plusDays = dayjs(removeTimezone(arrivalTime))
    .startOf("day")
    .diff(dayjs(removeTimezone(departureTime)).startOf("day"), "day");
  const plusDaysLabel =
    plusDays > 0
      ? t("flightArrivalDifferentDay.plusDaysCount", {
          count: plusDays,
          defaultValue: `+${plusDays} day${plusDays === 1 ? "" : "s"}`,
        })
      : null;
  const arrivalDateLabel =
    plusDays !== 0
      ? formatDateTime(
          dayjs(removeTimezone(arrivalTime)).toDate(),
          DateTimeFormatStyle.MediumDate
        )
      : null;

  return (
    <div className={clsx(block, props.className)}>
      <div className={cn("time-line")}>
        <div className={cn("left")}>
          <div className={cn("time")}>
            {formatDateTime(
              dayjs(removeTimezone(departureTime)).toDate(),
              DateTimeFormatStyle.ShortTime
            )}
          </div>
          <Icon iconName={IconNameEnum.airplane} />
          <div className={cn("time")}>
            {formatDateTime(
              dayjs(removeTimezone(arrivalTime)).toDate(),
              DateTimeFormatStyle.ShortTime
            )}
            {plusDaysLabel != null ? (
              <span className={cn("plus-days")}>{plusDaysLabel}</span>
            ) : null}
          </div>
        </div>
        <span>
          {formatInterval(
            dayjs(arrivalTime).diff(dayjs(departureTime), "minute")
          )}
        </span>
      </div>

      {arrivalDateLabel != null ? (
        <div className={cn("arrives-on")}>
          <Trans
            i18nKey="flightArrivalDifferentDay.flightArrivesOnDate"
            values={{ arrivalDate: arrivalDateLabel }}
            components={{ 1: <span className={cn("notification-date")} /> }}
          />
        </div>
      ) : null}

      <div className={cn("info-line")}>
        <div className={cn("left")}>
          <AirlineIcon
            size="small"
            className={cn("airline-icon")}
            airlineCode={airlineCode}
          />
          <span>
            {[airlineName, `${airlineCode} ${flightNumber}`, cabinClassName]
              .filter((item) => item != null && item !== "")
              .join(" • ")}
          </span>
        </div>
        <span>{t("stopDetails.nonstop")}</span>
      </div>
    </div>
  );
};
